import React, { useCallback, useState } from 'react';

import {
	CheckoutOrderFragment,
	CustomerFragment,
	PassengerFragment,
	TravellerFieldEnum,
	TravellerProfileFragment
} from '@websky/graphql';
import { TravellerDocumentFields, TravellerFields } from '../TravellerForm/types';
import { getActualPassengerValues } from './utils';
import { convertGenderToCorrectValue, getUserValue } from '../utils';
import { formatInvalidPassengerDate } from '../Passenger/utils';
import { SubmissionErrors } from 'final-form';
import { useToggleable } from '../hooks';
import { checkFfpStatusVariablesFactory, useFfpStatusCheck } from '../__fsd__/features/ffp-status-check';

export interface PassengerFileDocument {
	name: string;
	content: string;
	size: number;
}

export interface PassengerFilesState {
	changeFiles: PassengerFileDocument[];
}

interface PassengerDisabilitiesState {
	enabled: boolean;
	values: { [key: string]: boolean };
}

type PassengerValue = string | PassengerDisabilitiesState | PassengerFilesState;

interface CustomerProps {
	email?: string;
	phone?: string;
	accompanyingPersonName?: string;
	accompanyingPersonTicketNumber?: string;
	accompanyingCheck?: boolean;
	subscribed?: boolean;
}

type PassengerProps = Record<string, PassengerValue>;

export type MaxBirthDatesByPassengers = Map<string, { min: Date; max: Date }>;

export interface PassengerFormState {
	customer?: CustomerProps;
	passengers: PassengerProps[];
}

interface FillAction {
	type: 'fill';
	payload: {
		id: number;
		profileId: string;
		values: Map<string, string>;
	};
}

interface SwapAction {
	type: 'swap';
	payload: {
		id: number;
	};
}

export type ReducerActions = FillAction | SwapAction;

export const reducer: React.Reducer<PassengerFormState, ReducerActions> = (state, action): PassengerFormState => {
	if (action.type === 'fill') {
		return {
			...state,
			customer: {
				...(state.customer ?? {}),
				email: action.payload.values.get('email') ?? state.customer?.email,
				phone: action.payload.values.get('phone') ?? state.customer?.phone
			},
			passengers: state.passengers.map((passenger, index) => {
				if (index === action.payload.id) {
					const values: Record<string, string> = { ...passenger, profileId: action.payload.profileId };

					for (const key in values) {
						let value = values[key];

						const isDocField = [
							TravellerDocumentFields.DocExpiryDate,
							TravellerDocumentFields.DocNumber
						].includes(key as TravellerDocumentFields);

						if (action.payload.values.get(key) || isDocField) {
							value = action.payload.values.get(key);
						}

						values[key] = value;
					}

					return values;
				}

				return passenger;
			})
		};
	} else if (action.type === 'swap') {
		return {
			...state,
			passengers: state.passengers.map((passenger, index) => {
				if (index === action.payload.id) {
					return {
						...passenger,
						[TravellerFields.FirstName]: passenger[TravellerFields.LastName],
						[TravellerFields.LastName]: passenger[TravellerFields.FirstName]
					};
				}

				return passenger;
			})
		};
	}

	return state;
};

export const createInitialState = (
	passengers: PassengerFragment[],
	customer?: CustomerFragment
): PassengerFormState => {
	let customerValues: CustomerProps = {
		email: '',
		phone: '',
		accompanyingPersonName: '',
		accompanyingPersonTicketNumber: '',
		accompanyingCheck: false,
		subscribed: false
	};

	if (customer) {
		customerValues = {
			...customerValues,
			email: getUserValue(customer, TravellerFieldEnum.Email),
			phone: getUserValue(customer, TravellerFieldEnum.Phone),
			subscribed: customer.subscribed ?? false
		};
	}

	const passengerValues = passengers.map(passenger => {
		const values: PassengerProps = {
			type: passenger.type,
			disabilities: { enabled: false, values: {} },
			files: { changeFiles: [] }
		};

		passenger.values?.forEach(value => {
			let actualValue = value.value;

			switch (value.type) {
				case TravellerFieldEnum.Nationality:
					const hasValidationOption = value.validationRules?.[0]?.options?.some(
						option => option.value === value.value
					);

					if (!hasValidationOption && value.validationRules?.length) {
						actualValue = value.validationRules[0].options[0].value;
					}

					break;
				case TravellerFieldEnum.BirthDate:
				case TravellerFieldEnum.DocExpiryDate:
					actualValue = formatInvalidPassengerDate(value.type, actualValue);
					break;
				case TravellerFieldEnum.Gender:
					actualValue = convertGenderToCorrectValue(actualValue);
					break;
			}

			values[value.name] = actualValue;
		});

		return values;
	});

	return {
		customer: customerValues,
		passengers: passengerValues
	};
};

export const usePassengerForm = (
	passengers: PassengerFragment[],
	onFormSubmit: (values: object) => Promise<CheckoutOrderFragment | void>,
	customer?: CustomerFragment,
	isInternationalFlight?: boolean,
	isFreezeOrder?: boolean,
	showCheckDataModal?: boolean
) => {
	const {
		checkFfpStatus,
		isLoading: isCheckFfpStatusLoading,
		isEnabled: isFfpStatusCheckEnabled
	} = useFfpStatusCheck();

	const { isOpen: isCheckDataModalOpen, open: openCheckDataModal, close: closeCheckDataModal } = useToggleable(false);

	const [formValues, setFormValues] = useState<PassengerFormState>(null);

	const [state, dispatch] = React.useReducer(reducer, createInitialState(passengers, customer));

	const selectedProfileIdsByPassenger = React.useMemo(() => {
		const selectedProfileIdsByPassengerMap = new Map<number, string>();

		if (state.passengers.length) {
			state.passengers.forEach((passenger, index) => {
				if (passenger.profileId) {
					selectedProfileIdsByPassengerMap.set(index, passenger.profileId as string);
				}
			});
		}

		return selectedProfileIdsByPassengerMap;
	}, [state]);

	// TODO: fix passenger index to real passenger id
	const onFillValues = React.useCallback(
		(profile: TravellerProfileFragment, id: number, document?: string) => {
			if (profile) {
				const actualPassengerValues = getActualPassengerValues(
					passengers,
					profile,
					id,
					document,
					isInternationalFlight,
					isFreezeOrder
				);

				dispatch({ type: 'fill', payload: { id, profileId: profile.id, values: actualPassengerValues } });
			}
		},
		[state, passengers, isInternationalFlight, isFreezeOrder, dispatch]
	);

	const onSwapNames = React.useCallback((id: number) => dispatch({ type: 'swap', payload: { id } }), [state]);

	const onConfirm = async (values: object): Promise<void | SubmissionErrors> => {
		if (isFfpStatusCheckEnabled) {
			const formPassengers = (values as PassengerFormState).passengers as Array<Record<string, string | null>>;
			const passengersForCheck = checkFfpStatusVariablesFactory(
				passengers.map((passenger, i) => ({
					travellerId: passenger.id,
					values: passenger.values.map(field => ({
						...field,
						value: formPassengers[i]?.[field.name] ?? null
					})),
					loyaltyCode: formPassengers[i]?.['loyaltyNumber']
				}))
			);

			if (passengersForCheck.length) {
				try {
					const errors = await checkFfpStatus(passengersForCheck);

					if (errors) {
						const passengersErrors: SubmissionErrors = {};

						Object.entries(errors).forEach(([travellerId, errorMsg]) => {
							passengersErrors[travellerId] = { loyaltyNumber: errorMsg };
						});

						return { passengers: passengersErrors };
					}
				} catch (error) {
					return { error };
				}
			}
		}

		if (showCheckDataModal) {
			onShowCheckDataModal(values as PassengerFormState);

			return undefined;
		}

		onFormSubmit(values);
	};

	const onModalConfirm = useCallback(() => {
		closeCheckDataModal();
		onFormSubmit(formValues);
	}, [formValues]);

	const onShowCheckDataModal = useCallback(
		(values: PassengerFormState) => {
			setFormValues(values);
			openCheckDataModal();
		},
		[openCheckDataModal, setFormValues]
	);

	return {
		state,
		selectedProfileIdsByPassenger,
		isCheckFfpStatusLoading,
		isCheckDataModalOpen,
		onFillValues,
		onSwapNames,
		onConfirm,
		onModalConfirm,
		closeCheckDataModal
	};
};
